import { useState, useEffect, useRef } from 'react';
import './PortraitDebug.css';

const LAYER_ORDER = [
  'hair_tail',
  'hair_back',
  'face',
  'hair_base',
  'eyes',
  'ears',
  'hair_sides',
  'hair_fringes',
  'brows',
];

const HAIR_LAYERS = new Set(['hair_tail', 'hair_back', 'hair_base', 'hair_sides', 'hair_fringes']);
const EYE_LAYERS = new Set(['eyes']);

const PART_FILES = LAYER_ORDER.map(name => ({
  name,
  src: `/assets/parts/${name}.png`,
}));

const FRAME_SIZE = 64;

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

const HAIR_OUTLINE = [41, 32, 52];
const EYE_IRIS = [75, 105, 47];

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return [h, s, l];
}

function hslToRgb(h, s, l) {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1/3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1/3) * 255),
  ];
}

const OUTLINE_L = rgbToHsl(...HAIR_OUTLINE)[2];

function drawTinted(ctx, img, frameIdx, tintRgb, mode) {
  const offscreen = document.createElement('canvas');
  offscreen.width = FRAME_SIZE;
  offscreen.height = FRAME_SIZE;
  const octx = offscreen.getContext('2d');

  octx.drawImage(img, frameIdx * FRAME_SIZE, 0, FRAME_SIZE, FRAME_SIZE, 0, 0, FRAME_SIZE, FRAME_SIZE);

  const imageData = octx.getImageData(0, 0, FRAME_SIZE, FRAME_SIZE);
  const data = imageData.data;
  const [th, ts, tintL] = rgbToHsl(...tintRgb);

  let maxHairL = 0;
  if (mode === 'hair') {
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      if (r === HAIR_OUTLINE[0] && g === HAIR_OUTLINE[1] && b === HAIR_OUTLINE[2]) continue;
      const [, , l] = rgbToHsl(r, g, b);
      if (l > maxHairL) maxHairL = l;
    }
  }

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    const r = data[i], g = data[i + 1], b = data[i + 2];

    if (mode === 'hair') {
      if (r === HAIR_OUTLINE[0] && g === HAIR_OUTLINE[1] && b === HAIR_OUTLINE[2]) continue;
      const [, , l] = rgbToHsl(r, g, b);
      const clampedL = Math.max(OUTLINE_L * 1.1, l);
      const scaledL = (clampedL / maxHairL) * tintL;
      const finalL = Math.min(1, Math.max(OUTLINE_L * 1.1, scaledL));
      const finalS = tintL > 0 ? ts * (scaledL / tintL) : 0;
      const [nr, ng, nb] = hslToRgb(th, finalS, finalL);
      data[i] = nr; data[i + 1] = ng; data[i + 2] = nb;
    } else if (mode === 'eye') {
      if (!(r === EYE_IRIS[0] && g === EYE_IRIS[1] && b === EYE_IRIS[2])) continue;
      const [eh, es, el] = rgbToHsl(...tintRgb);
      const finalL = Math.max(OUTLINE_L * 1.1, el);
      const [nr, ng, nb] = hslToRgb(eh, es, finalL);
      data[i] = nr; data[i + 1] = ng; data[i + 2] = nb;
    }
  }

  octx.putImageData(imageData, 0, 0);
  ctx.drawImage(offscreen, 0, 0);
}

function PortraitDebug({ onBack }) {
  const [variantCounts, setVariantCounts] = useState({});
  const [selected, setSelected] = useState({});
  const [hairColor, setHairColor] = useState('#8b5e3c');
  const [eyeColor, setEyeColor] = useState('#3ca86b');
  const canvasRef = useRef(null);
  const imagesRef = useRef({});
  const rafRef = useRef(null);

  useEffect(() => {
    const counts = {};
    const images = {};
    let loaded = 0;

    for (const part of PART_FILES) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = part.src;
      // eslint-disable-next-line no-loop-func
      img.onload = () => {
        counts[part.name] = Math.round(img.naturalWidth / FRAME_SIZE);
        images[part.name] = img;
        loaded++;
        if (loaded === PART_FILES.length) {
          setVariantCounts({ ...counts });
          imagesRef.current = images;
          const init = {};
          for (const p of PART_FILES) {
            init[p.name] = ['hair_tail', 'hair_back'].includes(p.name) ? -1 : 0;
          }
          setSelected(init);
        }
      };
      // eslint-disable-next-line no-loop-func
      img.onerror = () => {
        loaded++;
        if (loaded === PART_FILES.length) {
          setVariantCounts({ ...counts });
        }
      };
    }
  }, []);

  useEffect(() => {
    if (Object.keys(selected).length === 0) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, FRAME_SIZE, FRAME_SIZE);

    const hairRgb = hexToRgb(hairColor);
    const eyeRgb = hexToRgb(eyeColor);

    for (const part of LAYER_ORDER) {
      const idx = selected[part];
      if (idx === -1 || idx === undefined) continue;
      const img = imagesRef.current[part];
      if (!img) continue;

      if (HAIR_LAYERS.has(part)) {
        drawTinted(ctx, img, idx, hairRgb, 'hair');
      } else if (EYE_LAYERS.has(part)) {
        drawTinted(ctx, img, idx, eyeRgb, 'eye');
      } else {
        ctx.drawImage(img, idx * FRAME_SIZE, 0, FRAME_SIZE, FRAME_SIZE, 0, 0, FRAME_SIZE, FRAME_SIZE);
      }
    }
    });
  }, [selected, hairColor, eyeColor]);

  function shift(part, delta) {
    const count = variantCounts[part] ?? 1;
    const min = ['hair_tail', 'hair_back'].includes(part) ? -1 : 0;
    const max = count - 1;
    setSelected(prev => {
      const next = (prev[part] ?? 0) + delta;
      return { ...prev, [part]: Math.max(min, Math.min(max, next)) };
    });
  }

  const controllable = LAYER_ORDER.filter(name => {
    const count = variantCounts[name] ?? 1;
    return count > 1 || name === 'hair_tail';
  });

  return (
    <div className="portrait-debug">
      <div className="pd-header">
        <button className="pd-back-btn" onClick={onBack}>← Back</button>
        <span className="pd-title">Portrait Viewer <span className="pd-dev-tag">[DEV]</span></span>
      </div>
      <div className="pd-body">
        <canvas
          ref={canvasRef}
          width={FRAME_SIZE}
          height={FRAME_SIZE}
          className="pd-canvas"
        />
        <div className="pd-controls">
          <div className="pd-color-row">
            <span className="pd-part-name">hair color</span>
            <input type="color" value={hairColor} onChange={e => setHairColor(e.target.value)} className="pd-color-picker" />
          </div>
          <div className="pd-color-row">
            <span className="pd-part-name">eye color</span>
            <input type="color" value={eyeColor} onChange={e => setEyeColor(e.target.value)} className="pd-color-picker" />
          </div>
          <div className="pd-divider" />
          {controllable.map(name => {
            const count = variantCounts[name] ?? 1;
            const idx = selected[name] ?? 0;
            const label = idx === -1 ? 'none' : String(idx);
            const atMin = idx <= (['hair_tail', 'hair_back'].includes(name) ? -1 : 0);
            const atMax = idx >= count - 1;
            return (
              <div key={name} className="pd-control-row">
                <span className="pd-part-name">{name}</span>
                <button className="pd-arrow" onClick={() => shift(name, -1)} disabled={atMin}>◀</button>
                <span className="pd-index">{label}</span>
                <button className="pd-arrow" onClick={() => shift(name, 1)} disabled={atMax}>▶</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PortraitDebug;
